/**
 * Cylindo Variant Toggle
 * Handles open/close toggle functionality for cylindo-variant-selector accordions
 * Matches TypeScript variant-select-cylindo.directive.ts behavior
 */

class CylindoVariantToggle {
  constructor(container) {
    this.container = container;
    this.toggleBtn = container.querySelector('[cylindo-open-toggle]');
    this.optionsContainer = container.querySelector('.cylindoVariant__options');
    this.selectedLabel = container.querySelector('.cylindoVariant__info--selected');
    this.openClass = 'cylindoVariant--open';
    
    if (!this.toggleBtn || !this.optionsContainer) {
      return;
    }
    
    this.init();
  }

  init() {
    // Toggle button click handler
    this.toggleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    });

    // Listen for variant changes to update selected value display
    // Matches TypeScript variant-select-cylindo.directive.ts line 83
    this.container.addEventListener('change', (e) => {
      this.updateDisplayValue(e);
    });
  }

  get isOpen() {
    return this.container.classList.contains(this.openClass);
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    // Check if options container has visible-overflow class
    const hasVisibleOverflow = this.optionsContainer.classList.contains('visible-overflow');
    const overflow = hasVisibleOverflow ? 'visible' : 'visible scroll';
    
    // Set initial height and overflow
    const currentHeight = this.optionsContainer.scrollHeight;
    this.optionsContainer.style.overflow = overflow;
    
    // Animate height to auto
    this.optionsContainer.style.height = '0';
    this.optionsContainer.offsetHeight; // Force reflow
    
    requestAnimationFrame(() => {
      this.optionsContainer.style.height = currentHeight + 'px';
      this.optionsContainer.style.transition = 'height 0.3s ease';
      
      // After animation, set to auto for dynamic content
      setTimeout(() => {
        if (this.isOpen) {
          this.optionsContainer.style.height = 'auto';
        }
      }, 300);
    });
    
    this.container.classList.add(this.openClass);
    this.ariaEnable();
    
    // Close other accordions in the same container (matches TypeScript onFieldsetOpen line 167-171)
    const parentContainer = this.container.closest('[custom-rugs]') || this.container.closest('[variant-select]');
    if (parentContainer) {
      const otherAccordions = parentContainer.querySelectorAll('[cylindo-variant-selector]');
      otherAccordions.forEach(accordion => {
        if (accordion !== this.container && accordion.cylindoToggleInstance && accordion.cylindoToggleInstance.isOpen) {
          accordion.cylindoToggleInstance.close();
        }
      });
    }
  }

  close() {
    // Store current height
    const currentHeight = this.optionsContainer.scrollHeight;
    this.optionsContainer.style.height = currentHeight + 'px';
    this.optionsContainer.style.overflow = 'hidden';
    this.optionsContainer.offsetHeight; // Force reflow
    
    // Animate to 0
    requestAnimationFrame(() => {
      this.optionsContainer.style.transition = 'height 0.3s ease';
      this.optionsContainer.style.height = '0';
    });
    
    this.container.classList.remove(this.openClass);
    this.ariaDisable();
    
    // Clean up after animation
    setTimeout(() => {
      if (!this.isOpen) {
        this.optionsContainer.style.height = '';
        this.optionsContainer.style.transition = '';
      }
    }, 300);
  }

  ariaEnable() {
    const labels = this.optionsContainer.querySelectorAll('label');
    labels.forEach(label => {
      const hasNumberInput = !!label.querySelector('input[type="number"]');
      if (!hasNumberInput) {
        label.tabIndex = 0;
      } else {
        const inputs = label.querySelectorAll('input[type="number"]');
        inputs.forEach(input => input.tabIndex = 0);
      }
    });

    // DON'T auto-focus on open - let user click to focus
    // TypeScript focuses, but we should only focus when user explicitly interacts
    // Auto-focus causes unwanted scrolling on page load
    // Focus will happen naturally when user tabs or clicks
  }

  ariaDisable() {
    const labels = this.optionsContainer.querySelectorAll('label');
    labels.forEach(label => {
      const hasNumberInput = !!label.querySelector('input[type="number"]');
      if (!hasNumberInput) {
        label.tabIndex = -1;
      } else {
        const inputs = label.querySelectorAll('input[type="number"]');
        inputs.forEach(input => input.tabIndex = -1);
      }
    });
  }

  enable() {
    if (this.toggleBtn) {
      this.toggleBtn.removeAttribute('disabled');
    }
  }

  disable() {
    if (this.toggleBtn) {
      this.toggleBtn.setAttribute('disabled', 'true');
    }
  }

  /**
   * Update the selected value display when variant option changes
   * Matches TypeScript variant-select-cylindo.directive.ts updateDisplayValue() line 319-330
   */
  updateDisplayValue(event, overrideText) {
    if (!this.selectedLabel) {
      return;
    }

    // Allow override text for manual updates
    if (typeof overrideText !== 'undefined') {
      this.selectedLabel.textContent = overrideText;
      return;
    }

    const target = event.target;
    
    // Handle radio input changes
    if (target && target.type === 'radio' && target.checked) {
      const labelElement = target.closest('label');
      if (labelElement) {
        // For custom rugs - get title from specific elements (matches TypeScript behavior)
        // Finishing: .custom-rug-option__finishing--title
        // Rug Pad: .custom-rug-option__pad--title  
        // Color/Shape/Size: .custom-rug-option__label
        const finishingTitle = labelElement.querySelector('.custom-rug-option__finishing--title');
        const rugPadTitle = labelElement.querySelector('.custom-rug-option__pad--title');
        const optionLabel = labelElement.querySelector('.custom-rug-option__label');
        
        if (finishingTitle) {
          // For finishing, use only the title text (not description)
          this.selectedLabel.textContent = finishingTitle.textContent.trim();
          return;
        }
        
        if (rugPadTitle) {
          // For rug pad, use only the title text
          this.selectedLabel.textContent = rugPadTitle.textContent.trim();
          return;
        }
        
        if (optionLabel) {
          // For color/shape/size, use label text
          this.selectedLabel.textContent = optionLabel.textContent.trim();
          return;
        }
      }
      
      // Last resort: use input value
      if (target.value) {
        this.selectedLabel.textContent = target.value;
      }
    }
  }
}

// Initialize all cylindo variant toggles
function initializeCylindoToggles() {
  const containers = document.querySelectorAll('[cylindo-variant-selector]');
  containers.forEach(container => {
    // Don't re-initialize if already initialized
    if (container.cylindoToggleInstance) {
      return;
    }
    
    const toggle = new CylindoVariantToggle(container);
    if (toggle.toggleBtn) {
      container.cylindoToggleInstance = toggle;
      
      // DON'T auto-update display value on init - let user select first
      // TypeScript doesn't auto-update on init - only when user selects
      // The display value will be updated when user actually selects an option
    }
  });
}

// Also listen for variant changes globally (for dynamically added content)
function handleVariantChange(event) {
  const target = event.target;
  if (target && target.type === 'radio') {
    const container = target.closest('[cylindo-variant-selector]');
    if (container && container.cylindoToggleInstance) {
      container.cylindoToggleInstance.updateDisplayValue(event);
    }
  }
}

// Global change listener for all variant inputs
document.addEventListener('change', (e) => {
  if (e.target && e.target.type === 'radio' && e.target.closest('[cylindo-variant-selector]')) {
    handleVariantChange(e);
  }
});

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCylindoToggles);
} else {
  initializeCylindoToggles();
}

// Re-initialize on section load (for Shopify theme editor)
document.addEventListener('shopify:section:load', () => {
  initializeCylindoToggles();
});

// Export for external use
window.CylindoVariantToggle = CylindoVariantToggle;
window.initializeCylindoToggles = initializeCylindoToggles;

